/**
 * CCF-13 (#413) — client-side duplicate-session freeze store.
 *
 * When a duplicate ONLINE session is detected, the BFF freezes the session: it
 * returns `423 Locked` with `{ error: 'duplicate_session_block', block }` on
 * every route (except the small read/clear allow-list). A single openapi-fetch
 * middleware (wired in `api/client.ts`) captures that block into this tiny
 * observable store, so the app can render the "DO NOT PROCEED" freeze overlay
 * the moment ANY call is refused — without polling.
 *
 * The overlay is a UX affordance; the freeze itself is enforced SERVER-SIDE (the
 * two-layer rule), so this store can never be the thing that "unlocks" the
 * agent — only a Clear Session (server-side) does that.
 */

import type { DuplicateSessionBlock } from './session.js';

type Listener = (block: DuplicateSessionBlock | null) => void;

let current: DuplicateSessionBlock | null = null;
const listeners = new Set<Listener>();

/** The currently-active duplicate-session block, or null when not frozen. */
export function getDuplicateSessionBlock(): DuplicateSessionBlock | null {
  return current;
}

/** Record (or clear) the active block and notify subscribers. */
export function setDuplicateSessionBlock(
  block: DuplicateSessionBlock | null,
): void {
  current = block;
  for (const l of listeners) l(block);
}

/** Subscribe to block changes; returns an unsubscribe fn. */
export function subscribeDuplicateSessionBlock(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The 423 body shape the BFF returns for a duplicate-session freeze. */
interface DuplicateSessionBlockResponse {
  error?: string;
  block?: DuplicateSessionBlock;
}

/**
 * Inspect a fetched Response for the CCF-13 freeze and, if present, record it.
 * Clones the response (bodies are one-shot) so the caller can still read it.
 * A no-op for any non-423 / non-CCF-13 response.
 */
export async function noteDuplicateSessionFrom(
  response: Response,
): Promise<void> {
  if (response.status !== 423) return;
  try {
    const body = (await response
      .clone()
      .json()) as DuplicateSessionBlockResponse;
    if (body?.error === 'duplicate_session_block' && body.block) {
      setDuplicateSessionBlock(body.block);
    }
  } catch {
    /* non-JSON 423 — ignore */
  }
}
